import { createTheme } from '@mui/material/styles';
import { theme as commonTheme } from '@monorepo/common';
import { TOKENS } from '../styles/tokens';

/**
 * cpgTheme — CPGJobs brand theme.
 *
 * Deep-merges CPGJobs brand values onto the shared `@monorepo/common` theme via the
 * documented MUI `createTheme(baseTheme, overrides)` extension pattern. Only the v5
 * MUI components + `sx` reach this palette; v4 `makeStyles` JSS in `styles.ts` files
 * resolves `theme.*` against MUI v4 defaults, so hardcoded colors there are edited
 * directly (Phases 3–5). Values come from src/styles/tokens.ts.
 */
export const cpgTheme = createTheme(commonTheme, {
  palette: {
    primary: {
      main: TOKENS.accent,
      dark: TOKENS.accentDeep,
      contrastText: '#fff',
    },
    success: {
      main: TOKENS.go,
      dark: TOKENS.goDeep,
    },
    warning: {
      main: TOKENS.alert,
      dark: TOKENS.alertDeep,
    },
    text: {
      primary: TOKENS.ink,
      secondary: TOKENS.ink3,
    },
    divider: TOKENS.lineSoft,
    background: {
      default: '#fff',
    },
  },
  typography: {
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
    h1: { fontFamily: TOKENS.fontDisplay, fontWeight: 900 },
    h2: { fontFamily: TOKENS.fontDisplay, fontWeight: 800 },
    h3: { fontFamily: TOKENS.fontDisplay, fontWeight: 800 },
    h4: { fontFamily: TOKENS.fontDisplay, fontWeight: 800 },
    h5: { fontFamily: TOKENS.fontDisplay, fontWeight: 800 },
    h6: { fontFamily: TOKENS.fontDisplay, fontWeight: 800 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        contained: {
          border: `2px solid ${TOKENS.accent}`,
          boxShadow: `4px 4px 0 ${TOKENS.ink}`,
          '&:hover': {
            backgroundColor: TOKENS.accentDeep,
            boxShadow: `4px 4px 0 ${TOKENS.ink}`,
          },
          '&.Mui-disabled': {
            boxShadow: 'none',
          },
        },
        outlined: {
          border: `2px solid ${TOKENS.ink}`,
        },
      },
    },
    MuiInputLabel: {
      // Always float the label onto the border, even for empty selects, so every
      // field is visually consistent (mockup: the "Employment Type" treatment).
      defaultProps: {
        shrink: true,
      },
    },
    MuiOutlinedInput: {
      // Keep the outline notch open to match the always-shrunk label above.
      defaultProps: {
        notched: true,
      },
      styleOverrides: {
        notchedOutline: {
          borderColor: TOKENS.lineSoft,
          borderWidth: 1.5,
        },
        root: {
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${TOKENS.accentSoft}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TOKENS.ink,
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: TOKENS.placeholder,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: TOKENS.goSoft,
          color: TOKENS.goDeep,
        },
        standardWarning: {
          backgroundColor: TOKENS.alertSoft,
          color: TOKENS.alertDeep,
        },
        standardError: {
          backgroundColor: TOKENS.dangerBg,
          color: TOKENS.danger,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: TOKENS.ink,
          '.MuiTableCell-root': {
            color: TOKENS.chipFg,
            fontFamily: TOKENS.fontMono,
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
});

export default cpgTheme;
